import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane } from 'react-icons/fa';
import api, { adminAPI } from '../../services/api'; // Use centralized API service
import './ContactSection.css';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    budget: '',
    timeline: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });
    
    try {
      const submissionData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || 'Not provided',
        company: formData.company,
        service: formData.service || 'other',
        message: formData.message,
        budget: formData.budget || 'not-specified',
        timeline: formData.timeline || 'not-specified',
        source: 'contact-page'
      };

      console.log('📤 Sending data to backend (port 5025):', submissionData);
      
      // Using port 5025 through centralized API service
      const response = await api.post('/contacts/submit', submissionData);

      console.log('✅ Success response:', response.data);

      setMessage({
        text: response.data.message || 'Thank you for contacting us! We\'ll get back to you within 24 hours.',
        type: 'success'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: '',
        budget: '',
        timeline: ''
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 5000);

    } catch (error) {
      console.error('❌ Error details:', error);
      
      let errorMessage = 'Something went wrong. Please try again or email us directly.';
      
      if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Cannot connect to server. Please ensure backend is running on port 5025.';
      } else if (error.response) {
        console.error('Error response data:', error.response.data);
        
        if (error.response.status === 404) {
          errorMessage = 'API endpoint not found. Please check if /api/contacts/submit exists.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Cannot connect to server. Please check if backend is running on port 5025.';
      } else {
        errorMessage = error.message;
      }
      
      setMessage({
        text: errorMessage,
        type: 'error'
      });

      // Clear error message after 5 seconds
      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: <FaEnvelope />, title: 'Email Us', value: 'info@zustandtech.com', link: 'mailto:info@zustandtech.com' },
    { icon: <FaPhone />, title: 'Call Us', value: '+91 (919) 605-4906', link: 'tel:+919196054906' },
    // ✅ Updated address - Chinhat wala
    { icon: <FaMapMarkerAlt />, title: 'Visit Us', value: 'Kh No. 865, Devariya Khurd, Chinhat, Lucknow - 226028', link: 'https://maps.google.com/?q=Chinhat+Lucknow' }
  ];

  const serviceOptions = [
    { value: 'web-development', label: 'Web Development' },
    { value: 'mobile-apps', label: 'Mobile App Development' },
    { value: 'ecommerce', label: 'E-commerce Website' },
    { value: 'data-analytics', label: 'Data Analytics' },
    { value: 'ai-ml', label: 'AI/ML Solutions' },
    { value: 'cloud-solutions', label: 'Cloud Solutions' },
    { value: 'other', label: 'Other' }
  ];

  const budgetOptions = [
    { value: '<10k', label: 'Less than ₹10k' },
    { value: '10k-25k', label: '₹10k - ₹25k' },
    { value: '25k-50k', label: '₹25k - ₹50k' },
    { value: '50k-100k', label: '₹50k - ₹100k' },
    { value: '100k+', label: '₹100k+' },
    { value: 'not-specified', label: 'Not Specified' }
  ];

  const timelineOptions = [
    { value: 'immediate', label: 'Immediate (ASAP)' },
    { value: '1-3-months', label: '1-3 Months' },
    { value: '3-6-months', label: '3-6 Months' },
    { value: '6+months', label: '6+ Months' },
    { value: 'not-specified', label: 'Not Specified' }
  ];

  return (
    <section className="contact-section" id="contact">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="section-header"
        >
          <span className="section-subtitle">Get In Touch</span>
          <h2 className="section-title">
            Let's <span className="gradient-text">Connect</span>
          </h2>
          <p className="section-description">
            Ready to start your next project? Reach out to us and let's discuss how we can help.
          </p>
        </motion.div>

        <div className="contact-grid">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="contact-info"
          >
            <h3 className="info-title">We'd love to hear from you</h3>
            <p className="info-description">
              Whether you have a question about our services, pricing, or anything else, our team is ready to answer all your questions.
            </p>

            <div className="info-cards">
              {contactInfo.map((item, index) => (
                <motion.a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="info-card"
                  whileHover={{ x: 10 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="info-icon">{item.icon}</div>
                  <div className="info-details">
                    <span className="info-label">{item.title}</span>
                    <span className="info-value">{item.value}</span>
                  </div>
                </motion.a>
              ))}
            </div>

            <div className="office-hours">
              <h4>Office Hours</h4>
              <p>Wednesday - Monday: 10:00 AM - 6:00 PM</p>
              <p>Sunday: 10:00 AM - 5:00 PM</p>
              <p>Tuesday: Closed</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="contact-form-wrapper"
          >
                       <form className="contact-form" onSubmit={handleSubmit}>
              <h3 className="form-title">Tell us about your project</h3>
              
              {/* Row 1: Name + Email */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter Your Full Name"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter Your Email"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Row 2: Phone + Company */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter Your Phone Number (Optional)"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company Name</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Enter Your Company Name"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Row 3: Service + Budget */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="service">Service You Need *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    <option value="">Select a Service</option>
                    {serviceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="budget">Budget Range</label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Select Budget</option>
                    {budgetOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 4: Timeline (full width) */}
              <div className="form-group">
                <label htmlFor="timeline">Project Timeline</label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select Timeline</option>
                  {timelineOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Row 5: Project Details (full width) */}
              <div className="form-group">
                <label htmlFor="message">Project Details *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project requirements, goals, and any specific features you need..."
                  rows="5"
                  required
                  disabled={loading}
                ></textarea>
              </div>

              {message.text && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`message ${message.type}`}
                >
                  {message.text}
                </motion.div>
              )}

              <motion.button
                type="submit"
                className="btn btn-primary submit-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message <FaPaperPlane />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;